/**
 * Rendu Markdown MINIMAL et SÛR → nœuds React, SANS `dangerouslySetInnerHTML`.
 *
 * Choix assumé : aucune lib markdown (`react-markdown`…) n'est installée dans
 * l'ERP, et le contenu d'aide est un référentiel PRODUIT maîtrisé. On implémente
 * donc un sous-ensemble couvrant les besoins rédactionnels — titres, gras/italique,
 * `code`, listes à puces/numérotées, citations, liens, paragraphes — en construisant
 * des éléments React (React échappe le texte : aucune surface d'injection HTML).
 * Les liens sont filtrés (schémas http(s)/interne/#/mailto uniquement).
 */

import type { ReactNode } from "react";

// Schémas d'href autorisés : anti-injection (refuse `javascript:` & co).
function hrefAutorise(href: string): boolean {
  return (
    /^https?:\/\//i.test(href) ||
    href.startsWith("/") ||
    href.startsWith("#") ||
    href.startsWith("mailto:")
  );
}

// Une ligne est-elle la ligne de SÉPARATION d'un tableau GFM (`|---|:--:|`) ?
function estSeparateurTable(ligne: string): boolean {
  return /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)+\|?\s*$/.test(ligne);
}

// Découpe une ligne de tableau `| a | b |` en cellules, en retirant les `|` de bord.
function cellulesDe(ligne: string): string[] {
  return ligne
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cellule) => cellule.trim());
}

// Tokens inline reconnus : **gras**, `code`, *ital*, _ital_, [texte](url).
const INLINE_PATTERN = /(\*\*[^*]+\*\*)|(`[^`]+`)|(\*[^*]+\*)|(_[^_]+_)|(\[[^\]]+\]\([^)]+\))/g;
const LIEN_PATTERN = /^\[([^\]]+)\]\(([^)]+)\)$/;

/**
 * Rend le texte inline d'un bloc en nœuds React. Les tokens non reconnus (ou un
 * lien à href refusé) restent du texte pur.
 */
function rendreInline(texte: string, cle: string): ReactNode[] {
  const noeuds: ReactNode[] = [];
  let dernierIndex = 0;
  let compteur = 0;
  INLINE_PATTERN.lastIndex = 0;
  let correspondance = INLINE_PATTERN.exec(texte);
  while (correspondance !== null) {
    const token = correspondance[0];
    const debut = correspondance.index;
    if (debut > dernierIndex) noeuds.push(texte.slice(dernierIndex, debut));
    const k = `${cle}-${compteur}`;
    compteur += 1;
    if (token.startsWith("**")) {
      noeuds.push(<strong key={k}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("`")) {
      noeuds.push(
        <code
          key={k}
          className="rounded bg-champagne-100 px-1 py-0.5 text-[0.9em] text-aubergine-900"
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith("[")) {
      const lien = LIEN_PATTERN.exec(token);
      const libelle = lien?.[1];
      const href = lien?.[2];
      if (libelle !== undefined && href !== undefined && hrefAutorise(href)) {
        const externe = /^https?:/i.test(href);
        noeuds.push(
          <a
            key={k}
            href={href}
            className="text-champagne-700 underline hover:text-champagne-900"
            {...(externe ? { target: "_blank", rel: "noreferrer noopener" } : {})}
          >
            {libelle}
          </a>,
        );
      } else {
        noeuds.push(token);
      }
    } else {
      // *ital* ou _ital_
      noeuds.push(<em key={k}>{token.slice(1, -1)}</em>);
    }
    dernierIndex = debut + token.length;
    correspondance = INLINE_PATTERN.exec(texte);
  }
  if (dernierIndex < texte.length) noeuds.push(texte.slice(dernierIndex));
  return noeuds;
}

export interface MarkdownRendererProps {
  contenu: string;
}

export function MarkdownRenderer({ contenu }: MarkdownRendererProps) {
  const lignes = contenu.replace(/\r\n/g, "\n").split("\n");
  const blocs: ReactNode[] = [];
  let i = 0;
  let cle = 0;

  while (i < lignes.length) {
    const ligne = lignes[i];
    if (ligne === undefined) break;

    // Ligne vide → séparateur de blocs.
    if (ligne.trim() === "") {
      i += 1;
      continue;
    }

    // Bloc de code ``` … ```
    if (ligne.trim().startsWith("```")) {
      const tampon: string[] = [];
      i += 1;
      while (i < lignes.length) {
        const l = lignes[i];
        if (l === undefined || l.trim().startsWith("```")) break;
        tampon.push(l);
        i += 1;
      }
      i += 1; // saute la clôture ```
      blocs.push(
        <pre
          key={cle}
          className="overflow-x-auto rounded-lg bg-champagne-100 p-3 text-[13px] text-aubergine-900"
        >
          <code>{tampon.join("\n")}</code>
        </pre>,
      );
      cle += 1;
      continue;
    }

    // Tableau GFM : une ligne d'en-tête `| … |` SUIVIE d'une ligne de séparation
    // `|---|---|`. On les rend en `<table>` scrollable (les données métier —
    // dosage, pressurage, vieillissement — sont naturellement tabulaires).
    const ligneSuivante = lignes[i + 1];
    if (ligne.includes("|") && ligneSuivante !== undefined && estSeparateurTable(ligneSuivante)) {
      const entetes = cellulesDe(ligne);
      i += 2; // saute l'en-tête + la ligne de séparation
      const corps: string[][] = [];
      while (i < lignes.length) {
        const l = lignes[i];
        if (l === undefined || !l.includes("|") || l.trim() === "") break;
        corps.push(cellulesDe(l));
        i += 1;
      }
      blocs.push(
        <div key={cle} className="overflow-x-auto">
          <table className="w-full border-collapse text-[15px] text-champagne-900">
            <thead>
              <tr>
                {entetes.map((entete, idx) => (
                  <th
                    key={idx}
                    className="border-b border-champagne-300 px-3 py-2 text-left font-semibold text-aubergine-900"
                  >
                    {rendreInline(entete, `th-${cle}-${idx}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {corps.map((cellules, idxLigne) => (
                <tr key={idxLigne}>
                  {cellules.map((cellule, idxCol) => (
                    <td key={idxCol} className="border-b border-champagne-100 px-3 py-2 align-top">
                      {rendreInline(cellule, `td-${cle}-${idxLigne}-${idxCol}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      cle += 1;
      continue;
    }

    // Titres # … ###### (le titre de l'article est déjà un h1 hors markdown, on
    // plafonne donc la hiérarchie interne à h2/h3).
    const titre = /^(#{1,6})\s+(.*)$/.exec(ligne);
    if (titre) {
      const niveau = titre[1]?.length ?? 1;
      const texte = titre[2] ?? "";
      if (niveau <= 2) {
        blocs.push(
          <h2 key={cle} className="mt-6 font-serif text-[22px] font-semibold text-aubergine-900">
            {rendreInline(texte, `h-${cle}`)}
          </h2>,
        );
      } else {
        blocs.push(
          <h3 key={cle} className="mt-5 text-[17px] font-semibold text-aubergine-900">
            {rendreInline(texte, `h-${cle}`)}
          </h3>,
        );
      }
      cle += 1;
      i += 1;
      continue;
    }

    // Citation > …
    if (ligne.startsWith(">")) {
      const tampon: string[] = [];
      while (i < lignes.length) {
        const l = lignes[i];
        if (l === undefined || !l.startsWith(">")) break;
        tampon.push(l.replace(/^>\s?/, ""));
        i += 1;
      }
      blocs.push(
        <blockquote
          key={cle}
          className="border-l-4 border-champagne-300 pl-4 italic text-champagne-800"
        >
          {rendreInline(tampon.join(" "), `bq-${cle}`)}
        </blockquote>,
      );
      cle += 1;
      continue;
    }

    // Liste à puces - … / * …
    if (/^[-*]\s+/.test(ligne)) {
      const items: string[] = [];
      while (i < lignes.length) {
        const l = lignes[i];
        if (l === undefined || !/^[-*]\s+/.test(l)) break;
        items.push(l.replace(/^[-*]\s+/, ""));
        i += 1;
      }
      blocs.push(
        <ul key={cle} className="list-disc space-y-1 pl-6 text-[16px] text-champagne-900">
          {items.map((item, idx) => (
            <li key={idx}>{rendreInline(item, `ul-${cle}-${idx}`)}</li>
          ))}
        </ul>,
      );
      cle += 1;
      continue;
    }

    // Liste numérotée 1. …
    if (/^\d+\.\s+/.test(ligne)) {
      const items: string[] = [];
      while (i < lignes.length) {
        const l = lignes[i];
        if (l === undefined || !/^\d+\.\s+/.test(l)) break;
        items.push(l.replace(/^\d+\.\s+/, ""));
        i += 1;
      }
      blocs.push(
        <ol key={cle} className="list-decimal space-y-1 pl-6 text-[16px] text-champagne-900">
          {items.map((item, idx) => (
            <li key={idx}>{rendreInline(item, `ol-${cle}-${idx}`)}</li>
          ))}
        </ol>,
      );
      cle += 1;
      continue;
    }

    // Paragraphe : agrège les lignes consécutives « ordinaires ».
    const tampon: string[] = [];
    while (i < lignes.length) {
      const l = lignes[i];
      if (
        l === undefined ||
        l.trim() === "" ||
        l.trim().startsWith("```") ||
        /^(#{1,6})\s+/.test(l) ||
        l.startsWith(">") ||
        /^[-*]\s+/.test(l) ||
        /^\d+\.\s+/.test(l)
      ) {
        break;
      }
      tampon.push(l);
      i += 1;
    }
    blocs.push(
      <p key={cle} className="text-[16px] leading-relaxed text-champagne-900">
        {rendreInline(tampon.join(" "), `p-${cle}`)}
      </p>,
    );
    cle += 1;
  }

  return <div className="space-y-3">{blocs}</div>;
}
